// Evening planning (AV1) and klaarleggen (AV2) on the device: works offline, like Vandaag.

import { useLiveQuery } from "dexie-react-hooks";
import { planningDate } from "@shared/time";
import { checklistFor, type ChecklistCondition, type DayFacts } from "@shared/evening";
import { occurrencesBetween } from "@shared/recurrence";
import { pickTemplateId } from "@shared/rhythm";
import { uuidv7 } from "@shared/uuid";
import { db, getMeta, type ChecklistItem, type EveningSession, type PlanEntry, type Routine } from "./db";
import type { Me } from "./session";
import { sync } from "./sync";

const now = () => new Date().toISOString();

async function me() {
  return getMeta<Me>("me");
}

/** What a day is like, for klaarleggen: its day type, the gym on it, its appointments. */
async function factsOf(date: string): Promise<DayFacts> {
  const plan = await db.day_plans
    .where("plan_date")
    .equals(date)
    .filter((p) => !p.deleted_at)
    .first();
  let templateId = plan?.template_id ?? null;
  if (!plan) {
    const [templates, rules, overrides] = await Promise.all([
      db.day_templates.toArray(),
      db.day_template_rules.toArray(),
      db.day_overrides.toArray(),
    ]);
    templateId = pickTemplateId(date, templates, rules, overrides);
  }
  const entries = await db.plan_entries
    .where("plan_date")
    .equals(date)
    .filter((e) => !e.deleted_at && e.kind === "routine" && (e.status === "planned" || e.status === "done"))
    .toArray();
  const routines = await db.routines.bulkGet(entries.map((e) => e.routine_id!));
  const sportRoutine = routines.some((r) => r?.slot_kinds.includes("sport"));
  const [appointments, exceptions] = await Promise.all([db.appointments.toArray(), db.appointment_exceptions.toArray()]);
  const occurrences = occurrencesBetween(
    appointments.filter((a) => !a.deleted_at),
    exceptions.filter((e) => !e.deleted_at),
    date,
    date,
  );
  return { templateId, sportRoutine, appointmentTitles: occurrences.map((o) => o.title) };
}

export interface ChecklistRow {
  item: ChecklistItem;
  checkId: string | null;
}

async function checklistRows(date: string): Promise<ChecklistRow[]> {
  const [items, checks, facts] = await Promise.all([
    db.checklist_items.toArray(),
    db.checklist_checks
      .where("for_date")
      .equals(date)
      .filter((c) => !c.deleted_at)
      .toArray(),
    factsOf(date),
  ]);
  return checklistFor(items, facts).map((item) => ({ item, checkId: checks.find((c) => c.item_id === item.id)?.id ?? null }));
}

/** Klaarleggen for a date: the items that apply (AV2) with their checks for that date. */
export function useChecklist(date: string): ChecklistRow[] | undefined {
  return useLiveQuery(() => checklistRows(date), [date]);
}

/** The round of the block Klaarleggen tapped: every item for that date checked at once (B32). */
export async function checkAllFor(date: string): Promise<void> {
  for (const row of await checklistRows(date)) if (!row.checkId) await setChecked(row, date, true);
}

export async function setChecked(row: ChecklistRow, date: string, checked: boolean): Promise<void> {
  const id = row.checkId ?? uuidv7();
  const at = now();
  const user = await me();
  await sync.mutate("checklist.check", { id, itemId: row.item.id, forDate: date, checked, at }, [id], async () => {
    if (checked && !row.checkId) {
      await db.checklist_checks.add({
        id,
        space_id: row.item.space_id,
        user_id: user?.user.id ?? "",
        item_id: row.item.id,
        for_date: date,
        checked_at: at,
        version: 0,
        deleted_at: null,
        created_at: at,
        updated_at: at,
      });
    }
    if (!checked && row.checkId) await db.checklist_checks.update(row.checkId, { deleted_at: at });
  });
}

export function useChecklistItems(): ChecklistItem[] | undefined {
  return useLiveQuery(async () =>
    (await db.checklist_items.toArray()).filter((i) => !i.deleted_at).sort((a, b) => a.position - b.position),
  );
}

export async function saveChecklistItem(id: string | null, title: string, condition: ChecklistCondition, position: number): Promise<void> {
  const itemId = id ?? uuidv7();
  const user = await me();
  const at = now();
  await sync.mutate("checklist.saveItem", { id: itemId, title: title.trim(), condition, position }, [itemId], async () => {
    if (id) {
      await db.checklist_items.update(id, { title: title.trim(), condition, position });
    } else {
      await db.checklist_items.add({
        id: itemId,
        space_id: user?.settings?.personal_space_id ?? "",
        user_id: user?.user.id ?? "",
        title: title.trim(),
        condition,
        position,
        version: 0,
        deleted_at: null,
        created_at: at,
        updated_at: at,
      });
    }
  });
}

export async function removeChecklistItem(id: string): Promise<void> {
  const at = now();
  await sync.mutate("checklist.removeItem", { id, at }, [id], async () => {
    await db.checklist_items.update(id, { deleted_at: at });
  });
}

/** The evening planning (or "Snel plannen") for a date, if it was started. */
export function useSession(forDate: string, mode: EveningSession["mode"]): EveningSession | null | undefined {
  return useLiveQuery(
    async () =>
      (
        await db.evening_sessions
          .where("for_date")
          .equals(forDate)
          .filter((s) => !s.deleted_at && s.mode === mode)
          .toArray()
      )[0] ?? null,
    [forDate, mode],
  );
}

/** Starts the flow or remembers the step (AV1 AC3: stopping halfway keeps the step). Returns the session id. */
export async function saveStep(
  session: EveningSession | null,
  forDate: string,
  mode: EveningSession["mode"],
  step: number,
): Promise<string> {
  const id = session?.id ?? uuidv7();
  const at = now();
  const user = await me();
  await sync.mutate("evening.save", { id, forDate, mode, step, at }, [id], async () => {
    if (session) await db.evening_sessions.update(id, { step });
    else
      await db.evening_sessions.add({
        id,
        space_id: user?.settings?.personal_space_id ?? "",
        user_id: user?.user.id ?? "",
        for_date: forDate,
        mode,
        step,
        started_at: at,
        finished_at: null,
        duration_seconds: null,
        version: 0,
        deleted_at: null,
        created_at: at,
        updated_at: at,
      });
  });
  return id;
}

/** Done: tomorrow is planned (AV1 AC2); the time it took is kept (AC7). */
export async function finishSession(id: string, startedAt: string, forDate: string, mode: EveningSession["mode"]): Promise<void> {
  const at = now();
  const durationSeconds = Math.max(0, Math.min(86_400, Math.round((Date.parse(at) - Date.parse(startedAt)) / 1000)));
  await sync.mutate("evening.finish", { id, forDate, mode, at, durationSeconds }, [id], async () => {
    await db.evening_sessions.update(id, { finished_at: at, duration_seconds: durationSeconds, step: 5 });
  });
}

/** A routine on a day by PK's choice (af en toe, gym backup); the gym backup gets its own time. */
export async function planRoutineOn(routine: Routine, date: string, dayBlockId: string | null, atLocal: string | null): Promise<void> {
  const entryId = uuidv7();
  const at = now();
  const plan = await db.day_plans
    .where("plan_date")
    .equals(date)
    .filter((p) => !p.deleted_at)
    .first();
  await sync.mutate(
    "routine.planOn",
    { entryId, routineId: routine.id, date, dayBlockId, atLocal, today: planningDate(new Date()) },
    [entryId],
    async () => {
      await db.plan_entries.add({
        id: entryId,
        space_id: routine.space_id,
        user_id: routine.user_id,
        plan_date: date,
        day_plan_id: plan?.id ?? "",
        day_block_id: dayBlockId,
        kind: "routine",
        task_id: null,
        routine_id: routine.id,
        occurrence_key: date,
        at_local: atLocal ? `${atLocal}:00` : null,
        origin: "proposal",
        title: routine.title,
        status: "planned",
        completed_at: null,
        carried_from_id: null,
        position: 0,
        version: 0,
        deleted_at: null,
        created_at: at,
        updated_at: at,
      });
    },
  );
}

/**
 * Gym on a day by PK's choice in step 2 (B28): in the sport block of that day when there is one, else at its own
 * time. With `swap`, that planned gym day of the same week goes off first (Ruilen), so the week stays at its goal.
 */
export async function planGymOn(routine: Routine, date: string, time: string, swap?: PlanEntry): Promise<void> {
  const entryId = uuidv7();
  const at = new Date().toISOString();
  const plan = await db.day_plans
    .where("plan_date")
    .equals(date)
    .filter((p) => !p.deleted_at)
    .first();
  const sport = plan
    ? (await db.day_blocks.where("day_plan_id").equals(plan.id).toArray()).find(
        (b) => !b.deleted_at && b.slot_kind === "sport" && !b.group_title,
      )
    : undefined;
  const atLocal = sport && sport.start_local.slice(0, 5) === time ? null : time;
  const swapBlock = swap?.day_block_id ? await db.day_blocks.get(swap.day_block_id) : undefined;
  const swapAlone =
    swapBlock?.slot_kind === "sport" &&
    !(await db.plan_entries.toArray()).some(
      (e) => e.day_block_id === swapBlock.id && e.id !== swap!.id && e.status === "planned" && !e.deleted_at,
    );
  const rowIds = [entryId, ...(swap ? [swap.id] : []), ...(sport ? [sport.id] : []), ...(swapAlone && swapBlock ? [swapBlock.id] : [])];
  await sync.mutate(
    "routine.planOn",
    {
      entryId,
      routineId: routine.id,
      date,
      dayBlockId: sport?.id ?? null,
      atLocal,
      swapEntryId: swap?.id,
      today: planningDate(new Date()),
    },
    rowIds,
    async () => {
      if (swap) await db.plan_entries.update(swap.id, { status: "skipped" });
      if (swapAlone && swapBlock?.status === "open") await db.day_blocks.update(swapBlock.id, { status: "skipped" });
      if (sport?.status === "skipped") await db.day_blocks.update(sport.id, { status: "open" });
      await db.plan_entries.add({
        id: entryId,
        space_id: routine.space_id,
        user_id: routine.user_id,
        plan_date: date,
        day_plan_id: plan?.id ?? "",
        day_block_id: sport?.id ?? null,
        kind: "routine",
        task_id: null,
        routine_id: routine.id,
        occurrence_key: date,
        at_local: atLocal ? `${atLocal}:00` : null,
        origin: "proposal",
        title: routine.title,
        status: "planned",
        completed_at: null,
        carried_from_id: null,
        position: 0,
        version: 0,
        deleted_at: null,
        created_at: at,
        updated_at: at,
      });
    },
  );
}
